import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { trpc } from "../../lib/api/trpc";

type DocPerf = {
  documentId: string;
  filename: string;
  status: string;
  chunkCount: number;
  retrievalCount: number;
  citationCount: number;
  citationRate: number;
  lastRetrievedAt: string | null;
  createdAt: string;
};

type DocPerfResponse = {
  documents: DocPerf[];
  summary: {
    totalDocuments: number;
    neverRetrieved: number;
    highPerformers: number;
  };
};

type SortKey = keyof DocPerf;
type SortDir = "asc" | "desc";

function CitationRateBar({
  rate,
  hasRetrievals,
}: {
  rate: number;
  hasRetrievals: boolean;
}) {
  const pct = Math.round(rate * 100);

  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 rounded-full bg-[var(--track)] overflow-hidden">
        <div
          className="h-full rounded-full bg-[var(--accent)]"
          style={{ width: `${hasRetrievals ? Math.max(pct, 4) : 0}%` }}
        />
      </div>
      <span className="font-mono text-[12px] text-[var(--text-3)] w-8 text-right">
        {hasRetrievals ? `${pct}%` : "-"}
      </span>
    </div>
  );
}

function SortHeader({
  label,
  sortKey,
  currentSort,
  currentDir,
  onSort,
  numeric = false,
}: {
  label: string;
  sortKey: SortKey;
  currentSort: SortKey;
  currentDir: SortDir;
  onSort: (key: SortKey) => void;
  numeric?: boolean;
}) {
  const isActive = currentSort === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px] cursor-pointer select-none${
        numeric ? " text-right" : ""
      }`}
    >
      {label}
      {isActive && (
        <span className="ml-1">
          {currentDir === "asc" ? "\u25B2" : "\u25BC"}
        </span>
      )}
    </th>
  );
}

export function DocumentPerformancePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const range = (searchParams.get("range") || "30") as "7" | "30" | "90";
  const [showNeverRetrieved, setShowNeverRetrieved] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("retrievalCount");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // @ts-ignore - tRPC type inference through `as any` router cast
  const { data, isLoading, isError } =
    trpc.analytics.getDocumentPerformance.useQuery({ range });

  const result = data as DocPerfResponse | undefined;

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sortedDocs = useMemo(() => {
    if (!result) return [];
    let docs = [...result.documents];
    if (showNeverRetrieved) {
      docs = docs.filter((d) => d.retrievalCount === 0);
    }
    docs.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return docs;
  }, [result, showNeverRetrieved, sortKey, sortDir]);

  const handleRangeChange = (r: "7" | "30" | "90") => {
    setSearchParams({ range: r });
  };

  if (isLoading) {
    return (
      <div className="space-y-[18px] animate-pulse">
        <div className="grid grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] px-5 py-[18px]"
            >
              <div className="h-4 w-20 bg-[var(--surface-2)] rounded mb-3" />
              <div className="h-8 w-16 bg-[var(--surface-2)] rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-[var(--err-bg)] border border-[var(--err-bg)] text-[var(--err-text)] rounded-[12px] p-4">
        <p className="text-sm">Failed to load document performance data.</p>
      </div>
    );
  }

  if (!result || result.summary.totalDocuments === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-[var(--text-3)]">
          No indexed documents yet. Upload and index documents to see
          performance data.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-[18px]">
      {/* Range selector */}
      <div className="flex justify-end">
        <div className="inline-flex bg-[var(--surface-2)] border border-[var(--border)] rounded-[9px] p-[3px]">
          {(["7", "30", "90"] as const).map((r) => (
            <button
              key={r}
              onClick={() => handleRangeChange(r)}
              className={
                range === r
                  ? "px-[13px] py-[5px] text-[12.5px] font-semibold text-[#F4F2E8] bg-[var(--accent)] rounded-md transition-colors"
                  : "px-[13px] py-[5px] text-[12.5px] text-[var(--text-3)] rounded-md hover:text-[var(--text-2)] transition-colors"
              }
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] px-5 py-[18px]">
          <div className="text-[13px] text-[var(--text-3)] mb-[10px]">
            Total Indexed Documents
          </div>
          <div className="font-serif text-[36px] font-normal leading-none text-[var(--text)]">
            {result.summary.totalDocuments}
          </div>
        </div>
        <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] px-5 py-[18px]">
          <div className="flex items-center gap-2 mb-[10px]">
            <span className="inline-block w-[7px] h-[7px] rounded-full bg-[var(--warn-dot)]" />
            <span className="text-[13px] text-[var(--text-3)]">
              Never Retrieved
            </span>
          </div>
          <div className="font-serif text-[36px] font-normal leading-none text-[var(--warn-text)]">
            {result.summary.neverRetrieved}
          </div>
        </div>
        <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] px-5 py-[18px]">
          <div className="flex items-center gap-2 mb-[10px]">
            <span className="inline-block w-[7px] h-[7px] rounded-full bg-[var(--ok-dot)]" />
            <span className="text-[13px] text-[var(--text-3)]">
              High Performers
            </span>
          </div>
          <div className="font-serif text-[36px] font-normal leading-none text-[var(--ok-text)]">
            {result.summary.highPerformers}
          </div>
        </div>
      </div>

      {/* Filter toggle */}
      <div className="flex items-center justify-end">
        <label className="flex items-center gap-2 text-[12.5px] text-[var(--text-2)] cursor-pointer">
          <input
            type="checkbox"
            checked={showNeverRetrieved}
            onChange={(e) => setShowNeverRetrieved(e.target.checked)}
            className="w-[15px] h-[15px] rounded border-[1.5px] border-[var(--border-strong)] bg-[var(--surface)] text-[var(--accent)]"
          />
          Show only never-retrieved documents
        </label>
      </div>

      {/* Table */}
      <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] overflow-hidden overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-[var(--surface-2)] border-b border-[var(--border-2)]">
            <tr>
              <SortHeader
                label="Document"
                sortKey="filename"
                currentSort={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                label="Chunks"
                sortKey="chunkCount"
                currentSort={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
                numeric
              />
              <SortHeader
                label="Retrievals"
                sortKey="retrievalCount"
                currentSort={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
                numeric
              />
              <SortHeader
                label="Citations"
                sortKey="citationCount"
                currentSort={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
                numeric
              />
              <SortHeader
                label="Citation Rate"
                sortKey="citationRate"
                currentSort={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                label="Last Retrieved"
                sortKey="lastRetrievedAt"
                currentSort={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
            </tr>
          </thead>
          <tbody>
            {sortedDocs.map((doc) => (
              <tr
                key={doc.documentId}
                className="border-b border-[var(--border-2)] hover:bg-[var(--surface-2)] transition-colors"
              >
                <td className="px-5 py-3 text-[13px]">
                  <Link
                    to={`/documents/${doc.documentId}`}
                    className="text-[var(--accent-strong)] font-medium hover:underline"
                  >
                    {doc.filename}
                  </Link>
                </td>
                <td className="px-5 py-3 text-[13px] text-right font-mono text-[var(--text-2)]">
                  {doc.chunkCount}
                </td>
                <td className="px-5 py-3 text-[13px] text-right font-mono text-[var(--text)] font-medium">
                  {doc.retrievalCount}
                </td>
                <td className="px-5 py-3 text-[13px] text-right font-mono text-[var(--text-2)]">
                  {doc.citationCount}
                </td>
                <td className="px-5 py-3 text-[13px]">
                  <CitationRateBar
                    rate={doc.citationRate}
                    hasRetrievals={doc.retrievalCount > 0}
                  />
                </td>
                <td className="px-5 py-3 text-[13px] text-[var(--text-3)]">
                  {doc.lastRetrievedAt
                    ? formatDistanceToNow(new Date(doc.lastRetrievedAt), {
                        addSuffix: true,
                      })
                    : "Never"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
